import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import {
  Plus, Trash2, Send, CheckCircle, AlertCircle,
  Mail, ChevronDown, ChevronUp, X, Loader,
} from "lucide-react";

// ── API ──────────────────────────────────────────────────────────
const EMAIL_MARKETING_URL = "https://europe-west3-metarelm.cloudfunctions.net/emailMarketing";

// ── Design tokens (matches Acquisition palette) ──────────────────
const T = {
  bg:      "#FFFFFF",
  bgSoft:  "#F8F8FC",
  bgMuted: "#F0F0F6",
  dark:    "#0A0A0F",
  text:    "#1A1A2E",
  muted:   "#8080A0",
  dim:     "#B0B0C4",
  border:  "#E4E4EF",
  blue:    "#007BFF",
  red:     "#FF3D00",
  orange:  "#FF6B00",
  gold:    "#FFB800",
  white:   "#FFFFFF",
};
const GRAD      = `linear-gradient(90deg,${T.blue} 0%,${T.red} 40%,${T.orange} 70%,${T.gold} 100%)`;
const GRAD_SIZE = "300% 100%";

// ── CSS ──────────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:'DM Sans',sans-serif;background:#fff;color:${T.dark};}
  @keyframes gradFlow{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes spin{to{transform:rotate(360deg);}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
  @keyframes pulse{0%,100%{opacity:1;}50%{opacity:.5;}}
  .grad-text{background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 5s ease infinite;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
  .grad-bg{background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 4s ease infinite;}
  .em-input{width:100%;padding:11px 14px;background:${T.white};border:1.5px solid ${T.border};border-radius:10px;color:${T.dark};font-size:13.5px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .2s,box-shadow .2s;}
  .em-input:focus{border-color:${T.orange};box-shadow:0 0 0 3px rgba(255,107,0,.08);}
  .em-input::placeholder{color:#c0c0d0;}
  .em-textarea{width:100%;padding:11px 14px;min-height:80px;resize:vertical;background:${T.white};border:1.5px solid ${T.border};border-radius:10px;color:${T.dark};font-size:13.5px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .2s;}
  .em-textarea:focus{border-color:${T.orange};box-shadow:0 0 0 3px rgba(255,107,0,.08);}
  .em-textarea::placeholder{color:#c0c0d0;}
  .btn-primary{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;border-radius:40px;border:none;background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 4s ease infinite;color:#fff;font-family:'Sora',sans-serif;font-size:14px;font-weight:700;cursor:pointer;transition:transform .15s,box-shadow .2s;white-space:nowrap;}
  .btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(255,107,0,.35);}
  .btn-primary:disabled{opacity:.4;cursor:not-allowed;transform:none;animation:none;background:${T.muted};}
  .btn-ghost{display:inline-flex;align-items:center;gap:7px;padding:10px 18px;border-radius:40px;background:${T.bgSoft};border:1.5px solid ${T.border};color:${T.muted};font-family:'Sora',sans-serif;font-size:12px;font-weight:700;cursor:pointer;transition:background .2s,color .2s;}
  .btn-ghost:hover{background:${T.border};color:${T.dark};}
  .btn-icon{display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:8px;border:1.5px solid ${T.border};background:${T.white};cursor:pointer;transition:background .2s,border-color .2s;color:${T.muted};flex-shrink:0;}
  .btn-icon:hover{background:#fff0f0;border-color:#ffcccc;color:#e84020;}
  .btn-icon:disabled{opacity:.35;cursor:not-allowed;}
  .lbl{display:block;font-size:10px;font-weight:700;color:${T.orange};margin-bottom:6px;text-transform:uppercase;letter-spacing:.1em;font-family:'Sora',sans-serif;}
  .recipient-row{display:grid;grid-template-columns:1fr 1fr 38px;gap:10px;align-items:end;padding:14px 16px;border-radius:12px;background:${T.bgSoft};border:1.5px solid ${T.border};animation:fadeUp .2s ease;}
  .recipient-row.invalid{border-color:#ffcccc;background:#fff8f8;}
  .err-box{background:#fff5f5;border:1px solid #ffcccc;border-radius:10px;padding:12px 16px;color:#c0392b;font-size:13px;display:flex;align-items:center;gap:8px;}
  .success-banner{background:#f0fff4;border:1px solid #b2f5c8;border-radius:10px;padding:14px 18px;color:#1a7a40;font-size:13px;display:flex;align-items:center;gap:10px;}
  .status-chip{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;font-family:'Sora',sans-serif;}
  .status-chip.sent{background:#e6fff0;color:#1a7a40;}
  .status-chip.failed{background:#fff0f0;color:#c0392b;}
  .status-chip.pending{background:${T.bgMuted};color:${T.muted};}
  .collapse-btn{background:none;border:none;cursor:pointer;display:flex;align-items:center;gap:6px;color:${T.muted};font-family:'Sora',sans-serif;font-size:11px;font-weight:700;letter-spacing:.06em;padding:0;}
  .collapse-btn:hover{color:${T.dark};}
  .counter-badge{display:inline-flex;align-items:center;justify-content:center;min-width:22px;height:22px;padding:0 6px;border-radius:999px;background:${GRAD};background-size:${GRAD_SIZE};animation:gradFlow 4s ease infinite;color:#fff;font-size:10px;font-weight:700;font-family:'Sora',sans-serif;}
  @media(max-width:600px){.recipient-row{grid-template-columns:1fr 38px;}.recipient-row .name-col{grid-column:1/-1;}}
`;

// ── Types ────────────────────────────────────────────────────────
interface Recipient {
  id:     string;
  name:   string;
  email:  string;
}
interface SendResult {
  email:   string;
  success: boolean;
  error?:  string;
}

const newRecipient = (): Recipient => ({
  id:    Math.random().toString(36).slice(2),
  name:  "",
  email: "",
});

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

// ════════════════════════════════════════════════════════════════
//  COMPONENT
// ════════════════════════════════════════════════════════════════
const EmailMarketing: React.FC = () => {
  const user   = useSelector((s: RootState) => s.auth.user);
  const userId = user?.userId ?? "";

  // ── shared fields ─────────────────────────────────────────────
  const [subject,    setSubject]    = useState("");
  const [message,    setMessage]    = useState("");

  // ── recipients ────────────────────────────────────────────────
  const [recipients, setRecipients] = useState<Recipient[]>([newRecipient(), newRecipient()]);
  const [showAll,    setShowAll]    = useState(false);

  // ── send state ────────────────────────────────────────────────
  const [sending,   setSending]   = useState(false);
  const [results,   setResults]   = useState<SendResult[] | null>(null);
  const [errMsg,    setErrMsg]    = useState("");
  const [showValid, setShowValid] = useState(false);

  // ── derived ───────────────────────────────────────────────────
  const validRecipients  = recipients.filter(r => r.email.trim() && isValidEmail(r.email));
  const invalidRecipients = recipients.filter(r => r.email.trim() && !isValidEmail(r.email));
  const canSend = validRecipients.length > 0 && subject.trim() && message.trim() && !sending;

  const PREVIEW_COUNT = 4;
  const displayedRecipients = showAll ? recipients : recipients.slice(0, PREVIEW_COUNT);

  // ── helpers ───────────────────────────────────────────────────
  const updateRecipient = (id: string, patch: Partial<Recipient>) =>
    setRecipients(p => p.map(r => r.id === id ? { ...r, ...patch } : r));

  const removeRecipient = (id: string) =>
    setRecipients(p => p.filter(r => r.id !== id));

  const addRecipient = () => {
    setRecipients(p => [...p, newRecipient()]);
    if (!showAll && recipients.length >= PREVIEW_COUNT) setShowAll(true);
  };

  const pasteEmails = (raw: string) => {
    const parsed = raw
      .split(/[\n,;]+/)
      .map(s => s.trim())
      .filter(Boolean)
      .map(token => {
        // Handle "Name <email>" format
        const match = token.match(/^(.+?)\s*<(.+?)>$/);
        if (match) return { id: Math.random().toString(36).slice(2), name: match[1].trim(), email: match[2].trim() };
        return { id: Math.random().toString(36).slice(2), name: "", email: token };
      });
    if (parsed.length > 0) setRecipients(p => [...p, ...parsed]);
  };

  const resetAll = () => {
    setSubject(""); setMessage("");
    setRecipients([newRecipient(), newRecipient()]);
    setResults(null); setErrMsg(""); setShowAll(false); setShowValid(false);
  };

  // ── SEND ──────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!canSend) return;
    setSending(true); setErrMsg(""); setResults(null);

    try {
      const payload = {
        subject:    subject.trim(),
        message:    message.trim(),
        recipients: validRecipients.map(r => ({ name: r.name.trim() || undefined, email: r.email.trim() })),
        sentBy:     userId,
      };

      const res  = await fetch(EMAIL_MARKETING_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Failed to send emails");

      // Expect API to return { results: [{ email, success, error? }] }
      // Fall back to marking all as success if API doesn't return per-email results
      const apiResults: SendResult[] = data.results ?? validRecipients.map(r => ({ email: r.email, success: true }));
      setResults(apiResults);
    } catch (e: any) {
      setErrMsg(e.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  // ════════════════════════════════════════════════════════════════
  //  SUCCESS VIEW
  // ════════════════════════════════════════════════════════════════
  if (results) {
    const sent   = results.filter(r => r.success);
    const failed = results.filter(r => !r.success);

    return (
      <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif", padding:"40px 20px" }}>
        <style>{CSS}</style>
        <div style={{ maxWidth:620, margin:"0 auto" }}>

          {/* Hero */}
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <div className="grad-bg" style={{ width:72, height:72, borderRadius:"50%", margin:"0 auto 20px", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <CheckCircle size={36} color="#fff"/>
            </div>
            <p style={{ fontFamily:"'Sora',sans-serif", fontSize:11, letterSpacing:"0.3em", marginBottom:8 }}>
              <span className="grad-text">CAMPAIGN SENT</span>
            </p>
            <h2 style={{ fontFamily:"'Sora',sans-serif", fontSize:28, fontWeight:900, color:T.dark, marginBottom:8 }}>
              {sent.length} email{sent.length !== 1 ? "s" : ""} delivered 🎉
            </h2>
            <p style={{ color:T.muted, fontSize:14, lineHeight:1.7 }}>
              Subject: <strong style={{ color:T.dark }}>{subject}</strong>
            </p>
          </div>

          {/* Stats row */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:28 }}>
            {[
              { label:"Sent",   count:sent.length,   color:"#1a7a40", bg:"#e6fff0", border:"#b2f5c8" },
              { label:"Failed", count:failed.length, color:"#c0392b", bg:"#fff5f5", border:"#ffcccc" },
            ].map(({ label, count, color, bg, border }) => (
              <div key={label} style={{ background:bg, border:`1.5px solid ${border}`, borderRadius:14, padding:"18px 20px", textAlign:"center" }}>
                <div style={{ fontFamily:"'Sora',sans-serif", fontSize:32, fontWeight:900, color }}>{count}</div>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.12em", color, fontFamily:"'Sora',sans-serif" }}>{label.toUpperCase()}</div>
              </div>
            ))}
          </div>

          {/* Per-email results */}
          <div style={{ border:`1.5px solid ${T.border}`, borderRadius:14, overflow:"hidden", marginBottom:28 }}>
            <div style={{ padding:"12px 16px", background:T.bgSoft, borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:8 }}>
              <Mail size={13} color={T.muted}/>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.1em", color:T.muted, fontFamily:"'Sora',sans-serif" }}>DELIVERY REPORT</span>
            </div>
            <div style={{ maxHeight:300, overflowY:"auto" }}>
              {results.map((r, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"11px 16px", borderBottom: i < results.length - 1 ? `1px solid ${T.border}` : "none" }}>
                  <span style={{ fontSize:13, color:T.dark }}>{r.email}</span>
                  <span className={`status-chip ${r.success ? "sent" : "failed"}`}>
                    {r.success ? "✓ Sent" : "✗ Failed"}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
            <button className="btn-ghost" onClick={resetAll}>New Campaign</button>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════
  //  MAIN FORM
  // ════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif", paddingBottom:100 }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:"rgba(255,255,255,.95)", backdropFilter:"blur(12px)", borderBottom:`1px solid ${T.border}` }}>
        <div style={{ height:3, background:GRAD, backgroundSize:GRAD_SIZE, animation:"gradFlow 4s ease infinite" }}/>
        <div style={{ maxWidth:700, margin:"0 auto", padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div>
            <h1 style={{ fontFamily:"'Sora',sans-serif", fontWeight:900, fontSize:17, color:T.dark, lineHeight:1 }}>
              Email <span className="grad-text">Marketing</span>
            </h1>
            <p style={{ fontSize:11, color:T.muted, marginTop:3 }}>Send bulk campaigns to multiple recipients</p>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            {validRecipients.length > 0 && (
              <div style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", background:T.bgSoft, border:`1px solid ${T.border}`, borderRadius:999 }}>
                <span className="counter-badge">{validRecipients.length}</span>
                <span style={{ fontSize:11, color:T.muted, fontFamily:"'Sora',sans-serif", fontWeight:600 }}>valid</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:700, margin:"0 auto", padding:"28px 20px" }}>

        {/* ── Compose section ── */}
        <div style={{ marginBottom:28 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
            <div style={{ width:28, height:28, borderRadius:8, background:GRAD, backgroundSize:GRAD_SIZE, animation:"gradFlow 4s ease infinite", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <Mail size={13} color="#fff"/>
            </div>
            <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:15, color:T.dark }}>Compose</h2>
          </div>

          <div style={{ display:"grid", gap:14 }}>
            <div>
              <label className="lbl">Subject *</label>
              <input
                className="em-input"
                placeholder="e.g. Join our platform — free listing today"
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </div>
            <div>
              <label className="lbl">Message *</label>
              <textarea
                className="em-textarea"
                placeholder="Write your marketing message here…"
                value={message}
                onChange={e => setMessage(e.target.value)}
                style={{ minHeight:110 }}
              />
            </div>
          </div>
        </div>

        {/* ── Recipients section ── */}
        <div style={{ marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:28, height:28, borderRadius:8, background:T.bgMuted, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ fontSize:13 }}>👥</span>
              </div>
              <h2 style={{ fontFamily:"'Sora',sans-serif", fontWeight:800, fontSize:15, color:T.dark }}>Recipients</h2>
              {recipients.length > 0 && (
                <span style={{ fontSize:11, color:T.muted, fontFamily:"'DM Sans',sans-serif" }}>
                  {recipients.length} row{recipients.length !== 1 ? "s" : ""}{validRecipients.length !== recipients.length ? `, ${validRecipients.length} valid` : ""}
                </span>
              )}
            </div>

            {/* Paste hint */}
            <button
              className="btn-ghost"
              style={{ fontSize:11, padding:"7px 14px" }}
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText();
                  if (text) pasteEmails(text);
                } catch {
                  // clipboard permission denied — silently ignore
                }
              }}
              title="Paste emails from clipboard (comma, semicolon, or newline separated)"
            >
              Paste List
            </button>
          </div>

          {/* Column headers */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 38px", gap:10, padding:"0 16px", marginBottom:8 }}>
            <span style={{ fontSize:10, fontWeight:700, color:T.dim, letterSpacing:"0.1em", fontFamily:"'Sora',sans-serif" }}>NAME</span>
            <span style={{ fontSize:10, fontWeight:700, color:T.dim, letterSpacing:"0.1em", fontFamily:"'Sora',sans-serif" }}>EMAIL *</span>
            <span/>
          </div>

          {/* Rows */}
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <AnimatePresence initial={false}>
              {displayedRecipients.map((r) => {
                const isDirty  = r.email.trim().length > 0;
                const isInvalid = isDirty && !isValidEmail(r.email);
                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity:0, y:8 }}
                    animate={{ opacity:1, y:0 }}
                    exit={{ opacity:0, y:-6, height:0, marginBottom:0 }}
                    transition={{ duration:.18 }}
                  >
                    <div className={`recipient-row${isInvalid ? " invalid" : ""}`}>
                      <input
                        className="em-input"
                        placeholder="Full name (optional)"
                        value={r.name}
                        onChange={e => updateRecipient(r.id, { name: e.target.value })}
                        style={{ background:T.white }}
                      />
                      <div style={{ position:"relative" }}>
                        <input
                          className="em-input"
                          type="email"
                          placeholder="email@example.com"
                          value={r.email}
                          onChange={e => updateRecipient(r.id, { email: e.target.value })}
                          style={{ background:T.white, paddingRight: isDirty ? 32 : 14 }}
                        />
                        {isDirty && (
                          <div style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}>
                            {isInvalid
                              ? <AlertCircle size={13} color="#e84020"/>
                              : <CheckCircle size={13} color="#1a7a40"/>
                            }
                          </div>
                        )}
                      </div>
                      <button
                        className="btn-icon"
                        onClick={() => removeRecipient(r.id)}
                        disabled={recipients.length <= 1}
                        title="Remove"
                      >
                        <Trash2 size={13}/>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {/* Show more / less */}
          {recipients.length > PREVIEW_COUNT && (
            <button
              className="collapse-btn"
              style={{ marginTop:10, marginLeft:4 }}
              onClick={() => setShowAll(v => !v)}
            >
              {showAll
                ? <><ChevronUp size={13}/> Show less</>
                : <><ChevronDown size={13}/> Show {recipients.length - PREVIEW_COUNT} more</>
              }
            </button>
          )}

          {/* Add row */}
          <button className="btn-ghost" onClick={addRecipient} style={{ marginTop:12 }}>
            <Plus size={13}/> Add Recipient
          </button>

          {/* Invalid warning */}
          {invalidRecipients.length > 0 && showValid && (
            <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }}
              className="err-box" style={{ marginTop:14 }}>
              <AlertCircle size={14} color="#c0392b" style={{ flexShrink:0 }}/>
              {invalidRecipients.length} row{invalidRecipients.length > 1 ? "s have" : " has"} invalid email{invalidRecipients.length > 1 ? "s" : ""} and will be skipped.
            </motion.div>
          )}
        </div>

        {/* ── Summary bar ── */}
        {(subject || validRecipients.length > 0) && (
          <motion.div
            initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }}
            style={{ border:`1.5px solid ${T.border}`, borderRadius:12, padding:"14px 18px", background:T.bgSoft, marginBottom:20, display:"flex", flexWrap:"wrap", gap:16, alignItems:"center" }}>
            <div>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:3 }}>SUBJECT</div>
              <div style={{ fontSize:13, color:T.dark, fontWeight:600 }}>{subject || <span style={{ color:T.dim }}>—</span>}</div>
            </div>
            <div style={{ width:1, background:T.border, alignSelf:"stretch" }}/>
            <div>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:3 }}>RECIPIENTS</div>
              <div style={{ fontSize:13, color:T.dark, fontWeight:600 }}>{validRecipients.length} valid{invalidRecipients.length > 0 ? `, ${invalidRecipients.length} invalid` : ""}</div>
            </div>
            <div style={{ width:1, background:T.border, alignSelf:"stretch" }}/>
            <div>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.14em", color:T.muted, fontFamily:"'Sora',sans-serif", marginBottom:3 }}>MESSAGE</div>
              <div style={{ fontSize:13, color:T.dark, fontWeight:600 }}>{message.length > 0 ? `${message.length} chars` : <span style={{ color:T.dim }}>—</span>}</div>
            </div>
          </motion.div>
        )}

        {errMsg && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} className="err-box" style={{ marginBottom:20 }}>
            <AlertCircle size={14} color="#c0392b" style={{ flexShrink:0 }}/>
            {errMsg}
          </motion.div>
        )}
      </div>

      {/* ── Sticky footer ── */}
      <div style={{
        position:"fixed", bottom:0, left:0, right:0,
        background:"rgba(255,255,255,.97)", backdropFilter:"blur(12px)",
        borderTop:`1px solid ${T.border}`, padding:"14px 24px",
        display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, zIndex:100,
      }}>
        <div style={{ fontSize:12, color:T.muted }}>
          {!canSend && !sending && (
            <span>
              {!subject.trim() ? "Add a subject" : !message.trim() ? "Add a message" : validRecipients.length === 0 ? "Add at least one valid email" : ""}
            </span>
          )}
          {canSend && (
            <span style={{ color:T.dark }}>
              Ready to send to <strong>{validRecipients.length}</strong> recipient{validRecipients.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <button
          className="btn-primary"
          disabled={!canSend}
          onClick={() => { setShowValid(true); handleSend(); }}
          style={{ minWidth:160 }}
        >
          {sending ? (
            <>
              <div style={{ width:14, height:14, borderRadius:"50%", border:"2px solid rgba(255,255,255,.4)", borderTop:"2px solid #fff", animation:"spin .7s linear infinite" }}/>
              Sending…
            </>
          ) : (
            <>
              <Send size={14}/>
              Send {validRecipients.length > 0 ? `${validRecipients.length} ` : ""}Email{validRecipients.length !== 1 ? "s" : ""}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default EmailMarketing;
